import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CloudIcon from '@mui/icons-material/Cloud';
import { SvgIconComponent } from '@mui/icons-material';

export interface ServiceItem {
  title: string;
  description: string;
  details: string[];
  icon: SvgIconComponent;
  highlights: string[];
}

export const services: ServiceItem[] = [
  {
    title: 'Data Engineering',
    description:
      'We design and build production-grade data pipelines that process billions of rows for enterprise clients across CPG, retail, and financial services.',
    details: [
      'Three generations of ETL/ELT frameworks — from Python/Pandas to PySpark on Databricks with Delta Live Tables and Change Data Feed streaming',
      'Medallion architecture (Bronze/Silver/Gold) with config-driven column transformations, brand pivoting, and automated schema harmonization',
      'Deep expertise in retail measurement data (Nielsen, IRI/Circana, SPINS), consumer panel data (Kantar), survey data, and financial datasets',
      'Production pipelines for 20+ enterprise clients: Kimberly-Clark, Beam Suntory, Coty, Wella, General Mills, Mondelez, and more',
      'Rapid deployment playbooks that compress new ETL implementations from 6 weeks to 1 week',
    ],
    icon: StorageIcon,
    highlights: [
      'ETL/ELT Pipelines',
      'Snowflake & Databricks',
      'Medallion Architecture',
      'Data Governance',
      'Nielsen & Kantar',
      'Delta Live Tables',
    ],
  },
  {
    title: 'Software Engineering',
    description:
      'Full-stack application development from architecture through deployment. We build production SaaS platforms with modern frameworks and clean, maintainable code.',
    details: [
      'React 18 + TypeScript frontends with Material UI, Tailwind CSS, and responsive design for web and mobile',
      'Python (Flask) and .NET (ASP.NET Core) backends with 4-layer architecture: controllers, resource managers, services, and repository pattern',
      'Multi-tenant SaaS with role-based access control (RBAC), JWT authentication via AWS Cognito, and Stripe billing integration',
      'Database design across DynamoDB, PostgreSQL, BigQuery, and SQL Server with dual-backend support patterns',
      '180+ unit tests and 30+ integration tests per project — we ship with confidence',
    ],
    icon: CodeIcon,
    highlights: [
      'React & TypeScript',
      'Flask & .NET',
      'REST APIs',
      'Multi-Tenant SaaS',
      'RBAC & Auth',
      'Database Design',
    ],
  },
  {
    title: 'AI / ML Engineering',
    description:
      'We integrate generative AI into real production workflows — not just prototypes. From LLM-powered data enrichment to conversational analytics platforms.',
    details: [
      'Multi-provider LLM integration with OpenAI GPT-4, Anthropic Claude, Google Gemini, and local models via Ollama',
      'LangChain-powered RAG pipelines with vector databases for document Q&A, legal document extraction, and competitive intelligence',
      'AI-enriched data pipelines using Sentence-BERT embeddings, TF-IDF scoring, and LLM response caching for cost optimization',
      'Natural language analytics platform (AnswerRocket Max) with AI-powered market share analysis, KPI performance, trend detection, and metric drivers',
      'Multi-turn conversational AI with context management, semantic search, and structured data extraction',
    ],
    icon: PsychologyIcon,
    highlights: [
      'OpenAI & Gemini',
      'LangChain & RAG',
      'Vector Databases',
      'AI Analytics',
      'Anthropic Claude',
      'Data Enrichment',
    ],
  },
  {
    title: 'Cloud & Infrastructure',
    description:
      'We architect and deploy production infrastructure across AWS, Azure, and GCP — fully codified with Terraform, automated with CI/CD, and built for reliability.',
    details: [
      'AWS expertise: S3, Lambda, DynamoDB, Redshift, CloudFront, Cognito, API Gateway, Route53, ACM, IAM, SES, and CloudWatch',
      'Azure expertise: Data Factory, Synapse Analytics, Blob Storage, Azure SQL, Databricks, and Azure DevOps',
      'GCP expertise: BigQuery, Cloud Composer (Airflow), Vertex AI, Cloud Storage, and AI Studio',
      'Infrastructure as Code with Terraform + Terragrunt — S3 backend state, multi-environment (dev/staging/prod), and GitHub Actions CI/CD',
      'Serverless architectures: Flask on Lambda via apig-wsgi adapter, DynamoDB tables, CloudFront distributions, and zero-ops deployments',
    ],
    icon: CloudIcon,
    highlights: [
      'AWS / Azure / GCP',
      'Terraform IaC',
      'CI/CD Pipelines',
      'Serverless',
      'Databricks',
      'Multi-Cloud',
    ],
  },
];
